import { useEffect, useState } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { useUiStore } from "@/application/stores/uiStore";
import { useCompanionCoach } from "@/application/usecases/useCompanionCoach";
import { useLevelFlow } from "@/application/usecases/useLevelFlow";
import { getSharedContentLoader } from "@/application/usecases/usePreloadLevel";
import type { Level } from "@/domain/learning/model/level";
import { scheduleDrillsForRetention } from "@/domain/learning/retention-schedule";
import type { LevelStory } from "@/domain/story/model/level-story";
import type { CommandSet } from "@/domain/vim/model/command-set";
import { commandSetFromLevels } from "@/domain/vim/model/command-set";
import { playSe } from "@/infrastructure/assets/audio";
import { type BackgroundName, backgroundAssetPath } from "@/infrastructure/assets/paths";
import { preloadBackground, preloadLevelStoryAssets } from "@/infrastructure/assets/preloader";
import { ContentLoadError } from "@/infrastructure/content/loader";
import { Button } from "@/presentation/components/Button";
import { Icon } from "@/presentation/components/icons/Icon";
import { HeaderZone, MainZone } from "@/presentation/components/layout/Zones";
import { Modal } from "@/presentation/components/overlay/Modal";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { AdvScene } from "@/presentation/features/adv/AdvScene";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import { Companion } from "@/presentation/features/companion/Companion";
import { DrillErrorBoundary } from "@/presentation/features/drill/DrillErrorBoundary";
import { DrillView } from "@/presentation/features/drill/DrillView";
import type { KeystrokeOutcome } from "@/presentation/features/drill/keystroke-log";
import { SettingsPanel } from "@/presentation/features/settings/SettingsPanel";
import { StampAward } from "@/presentation/features/stamp/StampAward";
import { levelDisplayName, parseLevelNumber } from "@/presentation/i18n/labels";
import type { Route } from "@/presentation/router/route";
import "./LevelScreen.css";

interface LevelScreenProps {
  readonly levelId: string;
  readonly navigate: (route: Route) => void;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly retryable: boolean }
  | {
      readonly status: "loaded";
      readonly level: Level;
      readonly story: LevelStory;
      readonly commandSet: CommandSet;
    };

/**
 * S-2 段（`/level/:levelId`）。ビューごとに切り替わる中身は`useLevelFlow`が決める（計画12）。
 * `levelId`が1〜7以外ならT-24（screen-flow.md §8.2）でS-1へ即座に戻す。
 */
export function LevelScreen({ levelId, navigate }: LevelScreenProps) {
  const levelNumber = parseLevelNumber(levelId);
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [confirmingExit, setConfirmingExit] = useState(false);

  useEffect(() => {
    if (levelNumber === null || levelNumber < 1 || levelNumber > 7) {
      navigate({ name: "level-select" });
    }
  }, [levelNumber, navigate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attemptは再試行時に再取得をトリガーするための意図的な依存
  useEffect(() => {
    if (levelNumber === null) {
      return;
    }
    let cancelled = false;
    setLoad({ status: "loading" });
    // 門／稽古の段で始めた先読みと同一のローダーを使い、取得＋Zodパースを二重にしない（NFR-001）。
    const loader = getSharedContentLoader();

    Promise.all([loader.loadLevel(levelId), loader.loadLevelStory(levelId), loader.loadManifest()])
      .then(([level, story, manifestLevels]) => {
        if (cancelled) {
          return;
        }
        const commandSet = commandSetFromLevels(
          manifestLevels
            .filter((entry) => {
              const number = parseLevelNumber(entry.id);
              return number !== null && number <= levelNumber;
            })
            .map((entry) => entry.commands),
        );
        setLoad({ status: "loaded", level, story, commandSet });
        preloadLevelStoryAssets(story);
        // 稽古画面（V-2/V-3）自体の情景も、導入ADVを読んでいる間に先読みしておく
        // （NFR-001実測で、話→稽古の遷移時にこの画像が初読込されるとp95を超えることを確認したため）。
        preloadBackground(`dojo-${levelNumber}` as BackgroundName);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoad({ status: "error", retryable: error instanceof ContentLoadError });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [levelId, levelNumber, attempt]);

  if (levelNumber === null) {
    return null;
  }

  if (load.status === "loading") {
    return (
      <MainZone className="level-screen__loading">
        <p>読み込み中…</p>
      </MainZone>
    );
  }

  if (load.status === "error") {
    return (
      <MainZone>
        <div className="level-screen__error" role="alert">
          <p>教材を読み込めませんでした</p>
          <div className="level-screen__error-actions">
            {load.retryable && (
              <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
                再試行
              </Button>
            )}
            <Button variant="quiet" onClick={() => navigate({ name: "level-select" })}>
              稽古の段へ
            </Button>
          </div>
        </div>
      </MainZone>
    );
  }

  const { level, story, commandSet } = load;

  return (
    <LevelScreenBody
      level={level}
      story={story}
      commandSet={commandSet}
      navigate={navigate}
      confirmingExit={confirmingExit}
      setConfirmingExit={setConfirmingExit}
    />
  );
}

interface LevelScreenBodyProps {
  readonly level: Level;
  readonly story: LevelStory;
  readonly commandSet: CommandSet;
  readonly navigate: (route: Route) => void;
  readonly confirmingExit: boolean;
  readonly setConfirmingExit: (value: boolean) => void;
}

function LevelScreenBody({
  level,
  story,
  commandSet,
  navigate,
  confirmingExit,
  setConfirmingExit,
}: LevelScreenBodyProps) {
  const leaveToLevelSelect = () => navigate({ name: "level-select" });
  const { view, advance, completeDrill, completeExam } = useLevelFlow(level, leaveToLevelSelect);
  const [drillKey, setDrillKey] = useState(0);
  const modalTitleId = "level-screen-exit-confirm-title";
  const volume = useUiStore((state) => state.volume);
  const progress = useProgressStore((state) => state.progress);
  const { openPopover, showToast } = useOverlay();

  // 稽古/昇段試験の間だけ師範代が反応する。それ以外のビューでは`hints`/`drillId`を
  // 空にして渡す（フックはビュー種別に関わらず毎レンダー呼ぶ必要があるため、
  // 下のearly returnより前で呼ぶ）。
  const currentDrill = view.kind === "drill" || view.kind === "exam" ? view.drill : null;
  const coach = useCompanionCoach(
    story.companionReactions,
    currentDrill?.hints ?? [],
    currentDrill?.id ?? "",
    level.id,
  );

  // 稽古=keiko、昇段試験=shiken。話（ADV）は AdvScene 側で hanashi 等を鳴らす。
  const sceneBgm = view.kind === "drill" ? "keiko" : view.kind === "exam" ? "shiken" : null;
  useBgm(sceneBgm);

  // 既に朱印済みの段への昇段試験入場＝復習（F-3・screen-flow.md §5）。不合格・手数超過でも
  // 既存の朱印は失われない（F-005）ことを、不安にさせないよう入場時に明示する。
  useEffect(() => {
    if (view.kind === "exam" && progress.stamps.has(level.id)) {
      showToast({ kind: "info", message: "朱印は失われません" });
    }
  }, [view.kind, level.id, progress, showToast]);

  const rankName = levelDisplayName(level.id) ?? level.title;

  if (view.kind === "intro" || view.kind === "outro") {
    const chapter = view.kind === "intro" ? story.intro : story.outro;
    return <AdvScene chapter={chapter} onComplete={advance} />;
  }

  if (view.kind === "stamp") {
    return <StampAward levelId={level.id} onComplete={advance} />;
  }

  const lessonIndex =
    view.kind === "drill" ? level.lessons.findIndex((lesson) => lesson.id === view.lessonId) : -1;
  const lesson = lessonIndex >= 0 ? level.lessons[lessonIndex] : undefined;
  // 進捗表示も記憶定着の出題順に合わせる（JSON上の並びではなくスケジューラ後の順）。
  const scheduledLessonDrills =
    lesson !== undefined ? scheduleDrillsForRetention(lesson.drills) : [];
  const drillProgress =
    view.kind === "drill" && lesson
      ? {
          current: Math.max(
            1,
            scheduledLessonDrills.findIndex((entry) => entry.drill.id === view.drillId) + 1,
          ),
          total: scheduledLessonDrills.length,
        }
      : undefined;

  function handleOutcome(outcome: KeystrokeOutcome) {
    coach.onOutcome(outcome);
    if (outcome.kind === "confirmed") {
      // 1打の経路にI/Oを置かない（NFR-002）。打ち音の再生は次のタスクへ逃がし、
      // 打鍵→描画の同期経路をAudio再生で遅延させない。
      window.setTimeout(() => playSe("key", volume), 0);
    }
  }

  const levelNumber = parseLevelNumber(level.id);

  return (
    <>
      {/* 稽古中の段の情景（画一色だった稽古画面に、段ごとの道場の空気を薄く添える。D-115）。 */}
      {levelNumber !== null && (
        <img
          className="level-screen__background"
          src={backgroundAssetPath(`dojo-${levelNumber}` as BackgroundName)}
          width={1920}
          height={1080}
          alt=""
          aria-hidden="true"
        />
      )}
      <div className="level-screen__scrim" aria-hidden="true" />
      <HeaderZone>
        <button
          type="button"
          className="level-screen__back"
          onClick={() => setConfirmingExit(true)}
          aria-keyshortcuts="Escape Shift+Escape"
          title="終了: Shift+Esc（墨板の外では Esc だけでも可）"
        >
          <Icon name="back" decorative size={20} />
          <span>稽古の段へ</span>
          <kbd className="level-screen__back-key">⇧Esc</kbd>
        </button>
        <span className="level-screen__title">
          {view.kind === "exam" ? (
            <>
              {rankName} <span className="level-screen__exam-label">昇段試験</span>
              {view.total > 1 ? ` ${view.index + 1}/${view.total}` : ""}
            </>
          ) : (
            <>
              {`${rankName} · レッスン ${lessonIndex + 1}/${level.lessons.length}${lesson ? ` ${lesson.title}` : ""}`}
              {drillProgress ? ` · 問い ${drillProgress.current}/${drillProgress.total}` : ""}
            </>
          )}
        </span>
        {/* 帳（設定）はO-1の範囲（計画13）。 */}
        <button
          type="button"
          className="level-screen__gear"
          aria-label="設定を開く"
          onClick={openPopover}
        >
          <Icon name="gear" decorative size={20} />
        </button>
        <SettingsPanel />
      </HeaderZone>
      <MainZone className="level-screen__main">
        <DrillErrorBoundary onRetry={() => setDrillKey((n) => n + 1)}>
          {view.kind === "drill" && (
            <DrillView
              key={`${drillKey}-${view.drillId}`}
              drill={view.drill}
              commandSet={commandSet}
              variant="drill"
              onPassed={completeDrill}
              onOutcome={handleOutcome}
              onRequestExit={() => setConfirmingExit(true)}
              {...(drillProgress ? { progress: drillProgress } : {})}
            />
          )}
          {view.kind === "exam" && (
            <DrillView
              key={`${drillKey}-exam-${view.index}`}
              drill={view.drill}
              commandSet={commandSet}
              variant="exam"
              onPassed={completeExam}
              onOutcome={handleOutcome}
              onRequestExit={() => setConfirmingExit(true)}
            />
          )}
        </DrillErrorBoundary>
      </MainZone>
      <Companion
        activeReaction={coach.activeReaction}
        hints={currentDrill?.hints ?? []}
        revealedHintCount={coach.revealedHintCount}
        canRequestHint={coach.canRequestHint}
        onRequestHint={coach.requestHint}
      />
      <Modal open={confirmingExit} onClose={() => setConfirmingExit(false)} titleId={modalTitleId}>
        <h2 id={modalTitleId}>稽古を中断しますか？</h2>
        <p>ここまでの合格は保存されています。中断しますか？</p>
        <div className="level-screen__confirm-actions">
          <Button variant="primary" onClick={() => setConfirmingExit(false)}>
            つづける
          </Button>
          <Button variant="quiet" onClick={leaveToLevelSelect}>
            中断する
          </Button>
        </div>
      </Modal>
    </>
  );
}
